/**
 * @file 加入知识库弹窗 — 空间/团队/KB 纯函数（从 useKnowledgeBaseAddModalSpace 抽出）
 */
import { partition, groupBy, toPairs, find, flatMap } from 'lodash-es';
import { normalizeKbList } from '@/pages/aigc/pages/Agent/components/QAInput/KnowledgeBaseMention/utils/knowledgeBaseUtils';
import type {
  IKnowledgeBaseItem,
  IMembershipItem,
  ITeamOption,
} from '@/pages/aigc/pages/Agent/components/QAInput/KnowledgeBaseMention/hooks/useKnowledgeBaseApi';
import type { TKnowledgeSpace } from '../components/KbAddFormControls';

// ─── 类型 ───────────────────────────────────────────

export interface IKbTeamGroup {
  teamId: string;
  teamName: string;
  kbs: IKnowledgeBaseItem[];
}

export interface IKbUploadCatalog {
  personalKbs: IKnowledgeBaseItem[];
  teamGroups: IKbTeamGroup[];
}

// ─── 常量 ───────────────────────────────────────────

export const CREATABLE_TENANT_ROLES = new Set(['owner', 'admin', 'contributor']);

/** 弹窗内 knowledge-bases 列表缓存（30min） */
export const KB_ADD_MODAL_CATALOG_CACHE_KEY = 'KbAddModalKnowledgeBasesCatalog';
export const KB_ADD_MODAL_AUTH_ME_CACHE_KEY = 'KbAddModalAuthMe';
export const KB_ADD_MODAL_CACHE_MS = 1800000;

// ─── 纯函数 ─────────────────────────────────────────

/** 对齐 WeKnora isDocumentKb */
export const isDocumentKb = (kb: IKnowledgeBaseItem): boolean => {
  if (!kb.kbId || !kb.kbName) return false;
  return kb.type !== 'faq';
};

const parseAuthPayload = (raw: unknown): Record<string, unknown> | null => {
  if (!raw || typeof raw !== 'object') return null;
  const root = raw as Record<string, unknown>;
  if (root.data && typeof root.data === 'object') {
    return root.data as Record<string, unknown>;
  }
  return root;
};

/** 个人 tenant：仅 user.tenant_id（对齐 WeKnora authStore.user.tenant_id） */
export const getPersonalTenantId = (authRaw: unknown): number | null => {
  const payload = parseAuthPayload(authRaw);
  const user = payload?.user as { tenant_id?: unknown; } | undefined;
  if (user?.tenant_id == null || user.tenant_id === '') return null;
  const id = Number(user.tenant_id);
  return Number.isNaN(id) ? null : id;
};

export const parseAuthMemberships = (authRaw: unknown): IMembershipItem[] => {
  const payload = parseAuthPayload(authRaw);
  const list = payload?.memberships;
  return Array.isArray(list) ? (list as IMembershipItem[]) : [];
};

const resolveTenantName = (
  memberships: IMembershipItem[],
  tenantId: number,
): string => {
  const membership = memberships.find((m) => m.tenant_id === tenantId);
  return membership?.tenant_name || String(tenantId);
};

/**
 * 对齐 useUploadKbCatalog.refresh（mode=document）
 * 全量 KB 不传 X-Tenant-ID，客户端按 tenant_id 分组
 */
export const buildUploadKbCatalog = (
  kbRaw: unknown,
  personalTenantId: number | null,
  memberships: IMembershipItem[],
): IKbUploadCatalog => {
  const allItems: IKnowledgeBaseItem[] = normalizeKbList(kbRaw)
    .filter(isDocumentKb)
    .map((item) => ({
      ...item,
      tenantId: item.tenantId ?? String(personalTenantId ?? ''),
    }));

  const [personalKbs, teamKbs] = partition(
    allItems,
    (item: IKnowledgeBaseItem) =>
      personalTenantId != null && Number(item.tenantId) === personalTenantId,
  );

  const teamGroups: IKbTeamGroup[] = toPairs(
    groupBy(teamKbs, 'tenantId'),
  ).map(([tenantId, kbs]: [string, IKnowledgeBaseItem[]]) => ({
    teamId: tenantId,
    teamName: resolveTenantName(memberships, Number(tenantId)),
    kbs,
  }));

  return { personalKbs, teamGroups };
};

/** 对齐 useKbSpaceOptions.teamTenantMemberships */
export const getCreatableTeamOptions = (
  memberships: IMembershipItem[],
  personalTenantId: number | null,
): ITeamOption[] =>
  memberships
    .filter(
      (m) => m.tenant_id !== personalTenantId && CREATABLE_TENANT_ROLES.has(m.role),
    )
    .map((m) => ({ value: String(m.tenant_id), text: m.tenant_name || String(m.tenant_id) }));

/** 对齐 CreateActionModal.resolveSelectedDocTenantId */
export const resolveExistingDocTenantId = (
  kbId: string,
  catalog: IKbUploadCatalog,
  space: TKnowledgeSpace,
  activeTeamId: string | undefined,
  personalTenantId: number | null,
): string | undefined => {
  const kb = find(
    [...catalog.personalKbs, ...flatMap(catalog.teamGroups, 'kbs')],
    { kbId },
  );
  if (kb?.tenantId) return kb.tenantId;

  if (space === 'personal') {
    return personalTenantId != null ? String(personalTenantId) : undefined;
  }

  if (activeTeamId) {
    const teamId = Number(activeTeamId);
    if (!Number.isNaN(teamId)) return String(teamId);
  }

  return personalTenantId != null ? String(personalTenantId) : undefined;
};

/** 对齐 resolveKbCreateTenantId */
export const resolveCreateKbTenantId = (
  space: TKnowledgeSpace,
  teamId: string | undefined,
  personalTenantId: number | null,
): string | undefined => {
  if (space === 'team' && teamId) return teamId;
  return personalTenantId != null ? String(personalTenantId) : undefined;
};

// ─── 表单工具函数 ──────────────────────────────────────

/** tea Form.Item label 占位符 */
export const EMPTY_LABEL = ' ';

/** 非空校验生成器 */
export const createRequiredValidator = (message: string, fieldName: string) => (
  values: Record<string, unknown>,
): string => (!String(values[fieldName] ?? '').trim() ? message : '');

/** 团队空间 teamId 校验生成器 */
export const createTeamIdValidator = (message: string) => (
  values: Record<string, unknown>,
): string => (
  values.space === 'team' && !String(values.teamId ?? '').trim() ? message : ''
);
